import { createPriceOptionsObject } from "./pricingController";
import IPricingOption from "../interfaces/IPricingOption";
import { isObjectEmpty } from "../utils/objectCheck";
// eslint-disable-next-line
import { menuItemAPICall } from "../components/menu/products/ItemsContainer";
import { HTTPMethods } from "../yoello-lib/modules/API/API";
import { uploadFileToS3 } from "./s3Controller";
import { IExtraTags } from "../components/util/handleTags";
import { IExtraGroup } from "../interfaces/IExtraGroup";
import { ISelectOptions } from "../yoello-lib/modules/dynamicForms/interfaces/IDynamicFormRow";
import { ITaxes } from "../interfaces/ITaxes";
import { IPreference } from "../components/menu/preferences/PreferenceForm";
import { getTaxesType } from "../yoello-lib/modules/utils/TaxController";
import { ITaxType } from "../yoello-lib/modules/interfaces/ITaxes";
import { IMenuData } from "../components/manageMenu/types";
import { ICallAPIFunction } from "../yoello-lib/modules/API2/types";

export interface IPricingOptionsObject {
    [key: string]: IPricingOption;
}

export const createMenuItemList = (menu: any) => {
    const newMenuArray = [];
    for (let i = 0; i < menu.length; i++) {
        const newObj = {
            ...menu[i],
            pricing_options: createPriceOptionsObject(menu[i].price, "po", "option_price")
        };
        newMenuArray.push(newObj);
    }
    return newMenuArray;
};

/**Creates a menu item object
 * Parses abv if needed
 * creates pricing optiosn object
 */
export const createMenuItemObject = (menuItemObject: any) => {
    let abv = 0;
    let error = false;
    // TODO: Fix this and type it properly
    try {
        if (menuItemObject.abv.length > 0) {
            abv = parseFloat(menuItemObject.abv) * 10;
        }
    } catch (error) {
    }
    const newMenuItem = {
        ...menuItemObject,
        abv,
    };
    const taxType = getTaxesType(menuItemObject.item_taxes);
    let key = "option_price";
    if (taxType === ITaxType.ADDITIVE) {
        key = "option_price_exclusive"
    }
    if (menuItemObject.pricing_options) {
        const pricing = createPriceOptionsObject(menuItemObject.pricing_options, "po", key);
        newMenuItem.pricing_options = pricing.priceOps;
        error = pricing.error;
    }
    if (menuItemObject.pricing_options_extras) {
        const pricing = createPriceOptionsObject(menuItemObject.pricing_options_extras, "poe", key);
        newMenuItem.pricing_options_extras = pricing.priceOps;
        error = pricing.error;
    }
    if (error) {
        return {
            newMenuItem: undefined,
            error: true
        };
    }
    // its only extra item when extras price option not be an empty object
    newMenuItem.sold_as_extra_only = (isObjectEmpty(newMenuItem.pricing_options) && !isObjectEmpty(newMenuItem.pricing_options_extras));

    return {
        newMenuItem,
        error: false
    };
};

export interface IMenuItemSubmitObject {
    id?: string;
    name_public: string;
    name_internal: string;
    description: string;
    abv: string;
    enabled?: boolean;
    delivery_enabled: boolean;
    table_service_enabled: boolean;
    collection_enabled: boolean;
    subcategory_id: string;
    item_vat_type: string;
    extraGroups: IExtraGroup[];
    extra_tags: IExtraGroup[];
    preference_tags: IPreference[];
    tags: string[] | IExtraTags<ISelectOptions>;
    url_photo: File | string | null;
    pricing_options?: IPricingOptionsObject;
    pricing_options_extras?: IPricingOptionsObject;
    item_taxes: ITaxes[];
    menus: IMenuData[];
    selectedExtraGroup: IExtraGroup[];
    is_extra: boolean;
    extra_groups?: IExtraGroup[];
}

export async function publishMenuItem(submitObject: IMenuItemSubmitObject, venueId: string,
    menuItemAPICall: menuItemAPICall,
    extraTagsAPICall: menuItemAPICall,
    menuItemRelationshipsCall: menuItemAPICall,
    retrieveToken: () => Promise<string | undefined>,
) {
    let menuId: string;
    const { newMenuItem, error } = createMenuItemObject(submitObject);
    if (error) {
        throw Error("Could not create object");
    }
    try {
        const preQuery = venueId;
        menuId = newMenuItem.id;
        delete newMenuItem?.id;
        const imageFile = typeof newMenuItem.url_photo === 'string' && newMenuItem.url_photo !== "delete" ? undefined : newMenuItem.url_photo;
        if (menuId === undefined) {
            delete newMenuItem?.url_photo;
            const result: any = await menuItemAPICall(HTTPMethods.POST, {
                body:
                    { ...newMenuItem, venue_id: venueId }, params: { venue_id: undefined },
            });
            if (!result.isSuccessful) {
                throw new Error("Menu item API failed");
            }
            menuId = result?.data?.id!;
            let uploadImg;
            if (imageFile) {
                uploadImg = await uploadFileToS3(imageFile, retrieveToken, "/menu-items/");
            }
            await menuItemAPICall(HTTPMethods.PATCH, { body: { url_photo: uploadImg?.url ?? null, venue_id: venueId }, query: menuId, params: { venue_id: undefined }, });

        }
        else {
            let url_photo = newMenuItem.url_photo ? newMenuItem.url_photo : undefined;
            if (imageFile) {
                const uploadImg = await uploadFileToS3(imageFile, retrieveToken, "/menu-items/");
                url_photo = uploadImg.url;
            }

            const result = await menuItemAPICall(HTTPMethods.PUT, { body: { ...newMenuItem, url_photo }, params: { venue_id: undefined }, query: menuId });
            if (!result.isSuccessful) {
                throw new Error("Menu item API failed");
            }
        }
        /**Promise handler for the menu items relationships */
        const workload = [];
        if (newMenuItem.item_taxes) {
            const query = menuId + "/taxes";
            workload.push(
                menuItemRelationshipsCall(HTTPMethods.PUT, { body: newMenuItem.item_taxes, query })
            );
        }
        if (newMenuItem.extra_items) {
            const query = menuId + "/menu_items_extras_groups";
            workload.push(
                menuItemRelationshipsCall(HTTPMethods.PUT, { body: newMenuItem.extra_items, query })
            );
        }
        if (newMenuItem.preference_tags) {
            const query = menuId + "/menu_item_preferences";
            workload.push(

                menuItemRelationshipsCall(HTTPMethods.PUT, { body: newMenuItem.preference_tags, query })
            );
        }
        await Promise.all(workload);
        if (newMenuItem.extra_tags && newMenuItem.extra_tags?.newTags) {
            const positionList = await extraTagsAPICall(HTTPMethods.GET, 
                {
                    query: menuId  + '/menu_items',
                });
            const newList = positionList as unknown;
            const ids = newMenuItem.extra_tags.newTags.map((tag: any) => tag.id);
            const sortBody = mapSortIdsToExtra(ids, newList as ISortObject[]);
            await extraTagsAPICall(HTTPMethods.PUT, {
                body: sortBody,
                query: menuId  + '/menu_items',
                preQuery
            });
        }
        return menuId;
    } catch (e) {
        return false;
    }
}

/**asigns/removes items to there extra groups  */
export async function assignExtraGroups(menuId: string, oldExtraGroups: IExtraGroup[], selectedExtraGroups: IExtraGroup[], callExtraAPI: ICallAPIFunction<IExtraGroup>) {
    const oldIds = oldExtraGroups?.map((group) => group.id);
    const remove = [];
    const add = [];
    for (const item of selectedExtraGroups) {
        const id = item.id;
        const index = oldIds?.findIndex((oldId) => oldId === id);
        if (index === -1) {
            add.push({
                menu_items_extras_group_id: id,
                menu_items: { append: [menuId] }
            });
        } else {
            oldIds.splice(index, 1);
        }
    }
    if (oldIds?.length > 0) {
        for (const ids of oldIds) {
            remove.push({
                menu_items_extras_group_id: ids,
                menu_items: { remove: [menuId] }
            })
        }
    }

    const body = [...add, ...remove];
    console.log(body);
    const result = await callExtraAPI(HTTPMethods.PATCH, {
        body
    })
    return result.isSuccessful;
}

interface ISortObject {
    deleted: boolean;
    id: string;
    menu_item_id: string;
    menu_items_extras_group_id: string;
    metadata: null;
    serial_id: number;
    sort_position: number;
}
function mapSortIdsToExtra(extraIds: string[], sortItems: ISortObject[]) {
    const newSortIdArray: string[] = [];
    for (const ids of extraIds) {
        for (const item of sortItems) {
            if (ids === item.menu_items_extras_group_id) {
                newSortIdArray.push(item.id);
            }
        }
    }
    return newSortIdArray;
}